import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Milestone as MilestoneIcon, Plus, Trash2, Calendar, User as UserIcon, AlertTriangle, Check, Clock, Ban, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { RichTextDisplay } from "@/components/RichTextEditor";
import SmartRichText from "@/components/SmartRichText";
import CommentThread from "@/components/CommentThread";

const STATUSES = ["planned", "in_progress", "done", "at_risk", "blocked"];
const STATUS_META = {
  planned: { icon: Clock, color: "text-muted-foreground" },
  in_progress: { icon: Clock, color: "text-primary" },
  done: { icon: Check, color: "text-emerald-500" },
  at_risk: { icon: AlertTriangle, color: "text-amber-500" },
  blocked: { icon: Ban, color: "text-red-500" },
};

export default function ProjectMilestonesTab({ projectId, projectMembers = [], myPerms = [] }) {
  const canCreate = myPerms.includes("milestone.create");
  const canUpdate = myPerms.includes("milestone.update");
  const canDelete = myPerms.includes("milestone.delete");

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [openMilestone, setOpenMilestone] = useState(null);
  // R97 · Comment counts per milestone id so rows can show a "N comments"
  // badge inline. One batch fetch after each list load.
  const [commentCounts, setCommentCounts] = useState({});

  const loadCounts = async (ids) => {
    if (!ids || ids.length === 0) { setCommentCounts({}); return; }
    try {
      const { data } = await api.post(`/threads/milestone/counts`, { entity_ids: ids });
      setCommentCounts(data || {});
    } catch { /* silent — counts are a nice-to-have */ }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/milestones`);
      setRows(data || []);
      loadCounts((data || []).map((r) => r.id));
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load milestones")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const patch = async (mid, p) => {
    try {
      await api.patch(`/milestones/${mid}`, p);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const remove = async (mid) => {
    if (!confirm("Delete this milestone?")) return;
    try { await api.delete(`/milestones/${mid}`); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const upcoming = useMemo(() => rows.filter((r) => r.status !== "done"), [rows]);
  const completed = useMemo(() => rows.filter((r) => r.status === "done"), [rows]);

  const memberName = (uid) => {
    const m = projectMembers.find((x) => x.user_id === uid);
    return m?.user?.name || m?.user?.email || "—";
  };

  return (
    <div className="space-y-6" data-testid="project-milestones-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Milestones</div>
          <p className="text-xs text-muted-foreground mt-1">Track delivery checkpoints, ownership, and risk status.</p>
        </div>
        {canCreate && (
          <NewMilestoneDialog projectId={projectId} projectMembers={projectMembers} onSaved={load} />
        )}
      </div>

      <Section title="Upcoming & active" empty="Nothing scheduled." rows={upcoming} memberName={memberName} canUpdate={canUpdate} canDelete={canDelete} patch={patch} remove={remove} testid="milestones-upcoming" projectMembers={projectMembers} onOpen={setOpenMilestone} commentCounts={commentCounts} />
      {completed.length > 0 && (
        <Section title="Completed" empty="" rows={completed} memberName={memberName} canUpdate={canUpdate} canDelete={canDelete} patch={patch} remove={remove} testid="milestones-completed" projectMembers={projectMembers} dimmed onOpen={setOpenMilestone} commentCounts={commentCounts} />
      )}

      {busy && rows.length === 0 && <div className="text-sm text-muted-foreground">Loading…</div>}

      {openMilestone && (
        <MilestoneDetailDialog
          milestone={openMilestone}
          projectMembers={projectMembers}
          memberName={memberName}
          canUpdate={canUpdate}
          onClose={() => setOpenMilestone(null)}
          onSaved={async () => { await load(); }}
        />
      )}
    </div>
  );
}

function Section({ title, rows, empty, memberName, canUpdate, canDelete, patch, remove, testid, projectMembers, dimmed = false, onOpen, commentCounts = {} }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow mb-2">{title} · {rows.length}</div>
      {rows.length === 0 && empty ? (
        <div className="text-xs text-muted-foreground">{empty}</div>
      ) : (
        <div className={`border border-border ${dimmed ? "opacity-70" : ""}`}>
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">Owner</div>
            <div className="col-span-2">Due</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {rows.map((m) => {
            const Icon = STATUS_META[m.status]?.icon || Clock;
            return (
              <div key={m.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`milestone-row-${m.id}`}>
                <div className="col-span-4">
                  <button
                    type="button"
                    onClick={() => onOpen?.(m)}
                    className="font-medium truncate text-left hover:text-primary"
                    data-testid={`milestone-open-${m.id}`}
                  >
                    {m.title}
                  </button>
                  {m.description && (
                    <div className="text-xs text-muted-foreground truncate" title="Open to view full description">
                      {stripHtml(m.description).slice(0, 100)}
                    </div>
                  )}
                </div>
                <div className="col-span-2 text-xs">
                  <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {memberName(m.owner_id)}</span>
                </div>
                <div className="col-span-2 text-xs">
                  {m.due_date ? <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {m.due_date}</span> : <span className="text-muted-foreground">—</span>}
                </div>
                <div className="col-span-2">
                  {canUpdate ? (
                    <Select value={m.status} onValueChange={(v) => patch(m.id, { status: v })}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`milestone-status-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <span className={`chip text-[10px] inline-flex items-center gap-1 ${STATUS_META[m.status]?.color || ""}`}>
                      <Icon className="w-3 h-3" /> {m.status?.replace("_", " ")}
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  {/* R97 · Comments badge — always visible; opens the detail dialog. */}
                  <button
                    type="button"
                    onClick={() => onOpen?.(m)}
                    className="inline-flex items-center gap-1 h-7 px-2 text-[10px] border border-transparent hover:border-border hover:bg-muted rounded-none"
                    title="Open comments"
                    data-testid={`milestone-comments-${m.id}`}
                  >
                    <MessageSquare className="w-3 h-3" />
                    {(commentCounts?.[m.id] || 0)}
                  </button>
                  {canDelete && (
                    <Button size="sm" variant="ghost" onClick={() => remove(m.id)} className="rounded-none h-7 px-2" data-testid={`milestone-delete-${m.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewMilestoneDialog({ projectId, projectMembers, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const [start, setStart] = useState("");
  const [status, setStatus] = useState("planned");

  const reset = () => { setTitle(""); setDesc(""); setOwner(""); setDue(""); setStart(""); setStatus("planned"); };

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/milestones`, {
        title, description: desc || null, owner_id: owner || null,
        start_date: start || null, due_date: due || null, status,
      });
      toast.success("Milestone added");
      setOpen(false); reset(); onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't add milestone")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-none" data-testid="milestone-add-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add milestone</Button>
      </DialogTrigger>
      <DialogContent data-testid="milestone-add-dialog">
        <DialogHeader><DialogTitle>New milestone</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><div className="label-eyebrow mb-1">Title <span className="text-amber-500">*</span></div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ms-add-title" /></div>
          <div><div className="label-eyebrow mb-1">Description</div>
            <SmartRichText value={desc} onChange={setDesc} mediaMode="url-only" workspaceId={activeWorkspaceId} minRows={2} maxHeight={280} placeholder="What's this milestone about? `/` for headings, `/ai` to polish." testid="ms-add-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="label-eyebrow mb-1">Owner</div>
              <Select value={owner || "_none"} onValueChange={(v) => setOwner(v === "_none" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="ms-add-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Unassigned</SelectItem>
                  {projectMembers.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><div className="label-eyebrow mb-1">Status</div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 text-sm" data-testid="ms-add-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><div className="label-eyebrow mb-1">Start date</div>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ms-add-start" /></div>
            <div><div className="label-eyebrow mb-1">Due date</div>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="ms-add-due" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="ms-add-submit">{busy ? "Saving…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

function MilestoneDetailDialog({ milestone, projectMembers, memberName, canUpdate, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [edit, setEdit] = useState(false);
  const [desc, setDesc] = useState(milestone.description || "");
  const [title, setTitle] = useState(milestone.title || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/milestones/${milestone.id}`, { title, description: desc });
      toast.success("Milestone updated");
      setEdit(false);
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="milestone-detail-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">{edit ? "Edit milestone" : milestone.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-auto pr-1">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="chip text-[10px] inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {milestone.status?.replace("_", " ")}
            </span>
            {milestone.due_date && (
              <span className="chip text-[10px] inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {milestone.due_date}
              </span>
            )}
            {milestone.owner_id && (
              <span className="chip text-[10px] inline-flex items-center gap-1">
                <UserIcon className="w-3 h-3" /> {memberName(milestone.owner_id)}
              </span>
            )}
          </div>

          {edit ? (
            <>
              <div>
                <div className="label-eyebrow mb-1">Title</div>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ms-detail-title" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">Description</div>
                <SmartRichText value={desc} onChange={setDesc} mediaMode="url-only" workspaceId={activeWorkspaceId} minRows={4} maxHeight={360} testid="ms-detail-desc" />
              </div>
              <div className="flex gap-2">
                <Button onClick={save} disabled={busy} className="rounded-none h-8 text-xs" data-testid="ms-detail-save">{busy ? "Saving…" : "Save"}</Button>
                <Button variant="ghost" onClick={() => setEdit(false)} className="rounded-none h-8 text-xs">Cancel</Button>
              </div>
            </>
          ) : (
            <div>
              <div className="label-eyebrow mb-1">Description</div>
              {milestone.description ? (
                <RichTextDisplay html={milestone.description} testid="ms-detail-desc-display" />
              ) : (
                <p className="text-xs text-muted-foreground">No description.</p>
              )}
              {canUpdate && (
                <Button variant="ghost" size="sm" className="rounded-none h-7 text-xs mt-2" onClick={() => setEdit(true)} data-testid="ms-detail-edit-btn">
                  Edit
                </Button>
              )}
            </div>
          )}
          {/* R97 · Comment thread always visible — even while editing so
              a discussion in progress doesn't get hidden by the description
              editor. Backend endpoint already exists (see routes_threads.py). */}
          <div className="pt-4 border-t border-border">
            <CommentThread entityType="milestone" entityId={milestone.id} title="Comments" emptyHint="Capture decisions and blockers right here — the whole team stays in the loop." testid={`ms-thread-${milestone.id}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
